import {
  Button,
  HorizontalLine,
  Loading,
  SearchBar,
  SearchIcon,
  useQParam,
  useToastContext,
} from '@trufla-technology/trumarket-react-design-system';
import './style.scss';
import { useHistory } from 'react-router-dom';
import {
  useListRequestDefinitions,
  useAssignRequestDefinitionsToTeams,
  useUnassignRequestDefinitionsFromTeams,
} from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import { ListRequestDefinitions } from '@trufla-technology/trufla-trudomain/dist/lib/truMarket/types';
import { useEffect, useRef, useState } from 'react';
import { differenceWith, isEqual } from 'lodash';
import { IErrorResponse } from 'app/types/ErrorResponse';
import { AssignRequestCard } from './AssignRequestCard';
type Props = {
  teamKey: string;
  creatingTeam?: boolean;
  getProduct: () => void;
};

type ListRequestDefinitionsCard = {
  experiences?: string;
  key?: string;
  productDefinitionKey?: string;
  productDefinitionName?: string;
  teamsExperience?: string;
  [key: string]: any;
};
export const AssignRequestsToProduct = ({
  teamKey,
  creatingTeam,
  getProduct,
}: Props) => {
  const history = useHistory();
  const toaster = useToastContext();
  const [myProducts, setMyProducts] = useState<ListRequestDefinitions>([]);
  const productDefinitionKey = useQParam('assign-requests');
  const productName = useQParam('name');
  const [selectedCount, setSelectedCount] = useState(0);

  const { data, loading } = useListRequestDefinitions({
    filter: { productDefinitionKey: productDefinitionKey || '' },
    paginate: { offset: 0, limit: 100 },
    select: [
      //@ts-ignore
      'experiences',
      'name',
      'key',
      'description',
      'productDefinitionKey',
      'productDefinitionName',
    ],
  });

  const [products, setProducts] = useState<ListRequestDefinitions>([]);

  const { data: savedProducts, get } = useListRequestDefinitions({
    select: [
      //@ts-ignore
      'teamsExperience',
      //@ts-ignore
      'experiences',
      'key',
      'productDefinitionKey',
      'productDefinitionName',
    ],
    filter: {
      productDefinitionKey: productDefinitionKey || '',
      teamKeys: [teamKey],
    },
    paginate: { offset: 0, limit: 100 },
  });

  const AssignRequestDefinitionsToTeams = useAssignRequestDefinitionsToTeams();
  const UnassignRequestDefinitionsFromTeams =
    useUnassignRequestDefinitionsFromTeams();

  const initialProducts = useRef(savedProducts);
  const [isAnyProductChanged, setIsAnyProductChanged] = useState(
    !isEqual(initialProducts.current, myProducts),
  );
  const [disabled, setDisabled] = useState(false);
  const comparableProps: (keyof ListRequestDefinitionsCard)[] = [
    'experiences',
    'key',
    'productDefinitionKey',
    'productDefinitionName',
    'teamsExperience',
  ];

  useEffect(() => {
    const comparingProductArr = (myProducts as ListRequestDefinitionsCard).map(
      (product: ListRequestDefinitionsCard) =>
        comparableProps.reduce((acc, prop) => {
          if (product[prop] !== undefined) {
            acc[prop] = product[prop];
          }
          return acc;
        }, {} as ListRequestDefinitionsCard),
    );

    if (!isEqual(initialProducts.current, comparingProductArr)) {
      setIsAnyProductChanged(true);
    } else setIsAnyProductChanged(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myProducts]);

  useEffect(() => {
    if (!data) return;
    setProducts([...products, ...data]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleSelect = (data: ListRequestDefinitions[number]) => {
    const selectedProduct = myProducts.find(
      (product) => product.key === data.key,
    );

    if (!!selectedProduct) {
      setMyProducts((prevState) => {
        let newState = [...prevState];
        newState = newState.filter((product) => product.key !== data.key);
        return newState;
      });
    } else {
      setMyProducts((prevState) => {
        return [...prevState, data];
      });
    }
  };

  const changeExperiences = (key: string, experience: string) => {
    const product = products.find((product) => product?.key === key);
    if (!product) {
      return;
    }
    if (!product.teamsExperience) {
      setMyProducts((prevState) => {
        return prevState.map((prev) => {
          if (prev.key === key)
            return { ...prev, teamsExperience: [{ teamKey, experience }] };
          else return prev;
        });
      });
    } else {
      const newExperience = product.teamsExperience?.map((exp) => {
        if (teamKey === exp.teamKey) {
          return { ...exp, experience };
        }
        return exp;
      });

      setMyProducts((prevState) => {
        return prevState.map((prev) => {
          if (prev.key === key) {
            return { ...prev, teamsExperience: newExperience };
          }
          return prev;
        });
      });
    }
  };

  const handleAddRequest = async () => {
    const assignRequests = differenceWith(
      myProducts,
      savedProducts || [],
      (
        productA: ListRequestDefinitions[number],
        productB: ListRequestDefinitions[number],
      ) =>
        productA.key === productB.key &&
        //@ts-ignore until truDomain
        productA?.teamsExperience[0]?.experience ===
          //@ts-ignore until truDomain
          productB?.teamsExperience[0]?.experience,
    );
    const unAssignRequests = differenceWith(
      savedProducts || [],
      myProducts,
      (
        productA: ListRequestDefinitions[number],
        productB: ListRequestDefinitions[number],
      ) =>
        productA.key === productB.key &&
        //@ts-ignore until truDomain
        productA?.teamsExperience[0]?.experience ===
          //@ts-ignore until truDomain
          productB?.teamsExperience[0]?.experience,
    );
    try {
      if (unAssignRequests.length) {
        await UnassignRequestDefinitionsFromTeams({
          unassigning: [
            ...unAssignRequests.map((request) => {
              return {
                teamKey: teamKey,
                requestDefinitionKey: request.key || '',
                //@ts-ignore untill truDomain
                experience: request.teamsExperience.find(
                  //@ts-ignore
                  (exp) => exp.teamKey === teamKey,
                ).experience,
              };
            }),
          ],
        });
      }
      if (assignRequests.length) {
        await AssignRequestDefinitionsToTeams({
          assigning: [
            ...assignRequests.map((request) => {
              return {
                teamKey: teamKey,
                requestDefinitionKey: request.key || '',
                // experience should be dynamic in the next step
                //@ts-ignore
                experience: request.teamsExperience.find(
                  //@ts-ignore
                  (exp) => exp.teamKey === teamKey,
                ).experience!,
              };
            }),
          ],
        });
      }
      if (unAssignRequests.length || assignRequests.length) {
        await get();
        await getProduct();
        toaster.success('Successfully changed!');
        history.replace({ search: '' });
      }
    } catch (err: unknown) {
      const errorMessage = (
        err as IErrorResponse
      )?.response?.data?.error.replaceAll('body.', '');
      toaster.error(errorMessage || `Error!, please try again.`);
    }
  };

  useEffect(() => {
    if (savedProducts) {
      setMyProducts(savedProducts);
      initialProducts.current = savedProducts;
    }
  }, [savedProducts, setMyProducts]);

  useEffect(() => {
    let count = 0;
    if (data) {
      count = data?.reduce((acc, product) => {
        const isSelected = myProducts
          .map((pro) => pro?.key)
          .includes(product?.key || '');
        return acc + (isSelected ? 1 : 0);
      }, 0);
    }
    setSelectedCount(count);
  }, [data, myProducts]);

  const handleSearch = (value: string) => {
    if (!value.length) setProducts(data!);
    else
      setProducts(
        products.filter((product: any) =>
          product.name.toLowerCase().includes(value.toLowerCase()),
        ),
      );
  };

  return (
    <div className="assign-request-to-product ">
      {loading && <Loading />}
      <div className="assign-request-to-product-scroll">
        {!loading && (
          <div className="header">
            <div>
              <p>{productName}</p>
              <p className="selected-count">{`(${selectedCount} from ${data?.length})`}</p>
            </div>
            {data && (
              <>
                {myProducts.length === data.length ? (
                  <button
                    type="button"
                    onClick={() => data && setMyProducts([])}
                  >
                    Unselect all
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => data && setMyProducts([...data])}
                  >
                    Select all
                  </button>
                )}
              </>
            )}
          </div>
        )}

        <article className="search-wrapper">
          <SearchBar
            icon={<SearchIcon />}
            placeholder="Search"
            onSearch={handleSearch}
          />
        </article>

        <HorizontalLine verticalMargin={'23px'} />

        {!loading &&
          products?.map((product) => {
            const selectedProduct = myProducts.find(
              (prod) => prod.key === product.key,
            );
            //@ts-ignore
            const selectedExperience = selectedProduct?.teamsExperience?.find(
              //@ts-ignore
              (exp) => exp.teamKey === teamKey,
            );
            const isSelected = myProducts
              .map((pro) => pro?.key)
              .includes(product?.key || '');

            return (
              <AssignRequestCard
                product={product}
                teamsExperience={selectedExperience}
                changeExperiences={changeExperiences}
                selected={isSelected}
                handleSelect={handleSelect}
                setDisabled={setDisabled}
              />
            );
          })}
      </div>

      <div className="products-actions">
        <Button
          type="primary"
          onClick={handleAddRequest}
          htmlType="button"
          disabled={disabled || !isAnyProductChanged}
        >
          {creatingTeam ? 'Add' : 'Change'}
        </Button>
        <Button
          classes="cancel-product-conf"
          onClick={() => history.replace({ search: '' })}
          htmlType="button"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
